import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { OfferRedeemResponse } from '@pv-frontend/gift-voucher-journey/interfaces/offer.interface';
import { OverrideStyleConfig } from '@pv-frontend/pv-shared-components/common/types';
import {
  CopyStyleOverride,
  PvCopyModule,
} from '@pv-frontend/pv-shared-components/copy';
import { PvDrawerModule } from '@pv-frontend/pv-shared-components/drawer';
import { PvEnumerateModule } from '@pv-frontend/pv-shared-directives/pipes/enumerate';
import { DeviceService } from '@pv-frontend/pv-shared-services/device-service';
import { SkeletonModule } from 'primeng/skeleton';
import { OfferData } from 'projects/visa-platform/src/app/shared/interfaces';
import { RedemptionService } from 'projects/visa-platform/src/app/shared/services/redemption.service';
import { environment } from 'projects/visa-platform/src/environments/environment';
import { Subject, takeUntil } from 'rxjs';

import { PROJECT_NAME } from '../../../constants';
import { ApiService } from '../../services/api.service';
import { CommonAnchorComponent } from '../common-anchor/common-anchor.component';

@Component({
  selector: 'visa-platform-claim-modal',
  imports: [
    PvCopyModule,
    PvDrawerModule,
    CommonModule,
    SkeletonModule,
    PvEnumerateModule,
    CommonAnchorComponent,
  ],
  templateUrl: './claim-modal.component.html',
  styleUrl: './claim-modal.component.scss',
})
export class ClaimModalComponent implements OnDestroy {
  public voucherCode: string | null = null;
  public copyIconStyle: CopyStyleOverride = {
    container: { justifyContent: 'right' },
    copyIconStyle: {
      fill: 'var(--text-primary)',
      height: '20px',
      width: '20px',
    },
  };
  public showDrawer: boolean = false;
  public pvDrawerStyleOverrides: OverrideStyleConfig = {
    borderRadius: this.deviceService.isCurrentDeviceMobile()
      ? '15px 15px 0 0'
      : '15px',
    overflow: 'auto',
    minWidth: this.deviceService.isCurrentDeviceMobile() ? '100%' : '',
    maxWidth: this.deviceService.isCurrentDeviceMobile() ? '' : '400px',
    minHeight: this.deviceService.isCurrentDeviceMobile() ? '' : '75vh',
    height: 'max-content',
  };
  public offerDetails?: Partial<OfferData>;
  public merchantLogo?: string;
  public merchantName?: string;
  public isLoading: boolean = false;
  public redeemFailed: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();
  public heading?: string;
  public projectName = PROJECT_NAME;
  public env = environment;
  private eligibilityRenewalId?: string;

  public constructor(
    private redemptionService: RedemptionService,
    private apiService: ApiService,
    public deviceService: DeviceService,
    private router: Router
  ) {
    this.redemptionService.redemptionModalVisible$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: boolean) => (this.showDrawer = state));
  }

  public redeem(): void {
    this.offerDetails = this.redemptionService.offerDetails;
    this.merchantLogo = this.redemptionService.merchantLogo;
    this.merchantName = this.redemptionService.merchantName;
    this.heading = this.redemptionService.modalHeading;
    this.voucherCode = this.redemptionService.voucherCode || '';
    this.eligibilityRenewalId = this.redemptionService.eligibilityRenewalId;

    if (this.heading) {
      return;
    }
    if (!this.offerDetails?.id) {
      return;
    }
    this.isLoading = true;
    this.redeemFailed = false;
    this.apiService
      .redeem(this.offerDetails.id, this.eligibilityRenewalId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (redeemRes: OfferRedeemResponse) => {
          this.isLoading = false;
          this.voucherCode = redeemRes?.voucher_code;
        },
        error: () => {
          this.isLoading = false;
          this.redeemFailed = true;
        },
      });
  }

  public handleClose(): void {
    this.redemptionService.hideRedemptionModal();
    if (this.heading) {
      this.router.navigate(['/order-history']);
    } else {
      this.redemptionService.triggerRefreshDetails();
    }
  }

  public handleRedemptionSteps(): void {
    if (
      this.router.url.startsWith(
        `/offer-details/${this.offerDetails?.id || ''}`
      )
    ) {
      this.redemptionService.hideRedemptionModal();
    } else {
      this.router.navigate(['/offer-details', this.offerDetails?.id || '']);
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
